/* =============================================================
   StudentAIAssistant.jsx — Student AI assistant (RAG-powered)
   -------------------------------------------------------------
   Chat interface that answers student questions using the
   university's knowledge base (uploaded documents).

   When the student's partner university is on Unixplore
   (linked_university_id is set), two tabs are shown:
     - Home university tab (own process type)
     - Partner university tab (partner's opposite type)

   Each tab has its own independent conversation history.
   Answers are generated by the ai-student-assistant Edge Function
   using RAG (semantic search over document_chunks + Gemini).
   ============================================================= */

import { useState, useRef, useEffect } from 'react';
import StudentLayout            from '../../components/StudentLayout';
import WorkInProgress          from '../../components/WorkInProgress';
import { useStudentContext }    from '../../hooks/useStudentContext';
import { useAuth }              from '../../context/AuthContext';
import { supabase }             from '../../lib/supabase';

/* ─── Suggested questions ────────────────────────────────────── */
const SUGGESTIONS_OUTGOING = [
  'What documents do I need to upload?',
  'What are the visa requirements?',
  'How do I find housing?',
  'What is the deadline for the learning agreement?',
  'How many ECTS credits do I need?',
  'What should I do before departure?',
];

const SUGGESTIONS_INCOMING = [
  'What documents must I submit on arrival?',
  'How do I register for courses?',
  'Is there on-campus housing available?',
  'What is the orientation schedule?',
  'How does the grading system work?',
  'Who is my contact at the university?',
];

function formatMessage(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}


/* ─── Single chat panel ──────────────────────────────────────── */
function ChatPanel({ universityId, type, uniName, fallbackSuggestions }) {
  const [messages, setMessages] = useState([{
    role: 'ai',
    text: `Hi! 👋 I'm the AI assistant for **${uniName}**. Ask me anything about the ${type} exchange process — I'll answer based on the official university documents.`,
  }]);
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [suggestions, setSuggestions] = useState(null); // null = loading
  const bottomRef                     = useRef(null);

  /* Fetch dynamic suggestions on mount */
  useEffect(() => {
    if (!universityId) { setSuggestions(fallbackSuggestions); return; }
    (async () => {
      try {
        const supabaseUrl = supabase.supabaseUrl;
        const { data: { session } } = await supabase.auth.getSession();
        const jwt = session?.access_token ?? '';
        const res = await fetch(`${supabaseUrl}/functions/v1/ai-student-assistant`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${jwt}`,
            'apikey':        supabase.supabaseKey,
          },
          body: JSON.stringify({ university_id: universityId, type, generate_suggestions: true }),
        });
        const data = await res.json();
        const list = data.suggestions ?? [];
        setSuggestions(list.length > 0 ? list : fallbackSuggestions);
      } catch {
        setSuggestions(fallbackSuggestions);
      }
    })();
  }, [universityId, type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const buildHistory = (msgs) =>
    msgs.slice(1).map(m => ({ role: m.role, text: m.text }));

  const send = async (text) => {
    const q = text.trim();
    if (!q || loading || !universityId) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);

    try {
      const supabaseUrl = supabase.supabaseUrl;
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token ?? '';

      const history = buildHistory(messages);

      const res = await fetch(`${supabaseUrl}/functions/v1/ai-student-assistant`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${jwt}`,
          'apikey':        supabase.supabaseKey,
        },
        body: JSON.stringify({ question: q, university_id: universityId, type, history }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error ?? `Error ${res.status}`);
      }

      setMessages(prev => [...prev, {
        role:      'ai',
        text:      data.answer,
        citations: data.citations ?? [],
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: `Sorry, I encountered an error: ${err.message}. Please try again or contact the International Office directly.`,
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Chat */}
      <div className="col-span-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col" style={{ height: '540px' }}>
          {/* Banner */}
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-indigo-50/60 rounded-t-2xl">
            <div className="w-8 h-8 rounded-xl bg-[#4343d5] flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-white text-[15px]">smart_toy</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0b1c30]">{uniName} Assistant</p>
              <p className="text-xs text-slate-400">Answers based on {uniName} official documents only</p>
            </div>
            <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Online
            </span>
          </div>

          {/* Messages */}
          <div className="flex-1 p-5 space-y-4 overflow-y-auto">
            {messages.map((msg, i) => (
              <div key={i} className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mb-0.5">
                    <span className="material-symbols-outlined text-[13px] text-indigo-600">smart_toy</span>
                  </div>
                )}
                <div className="max-w-[80%] flex flex-col gap-1.5">
                  <div
                    className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-[#4343d5] text-white rounded-br-sm'
                        : msg.isError
                          ? 'bg-red-50 text-red-700 border border-red-100 rounded-bl-sm'
                          : 'bg-slate-100 text-[#0b1c30] rounded-bl-sm'
                    }`}
                    dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                  />
                  {msg.role === 'ai' && msg.citations?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 px-1">
                      {msg.citations.map((cite, ci) => (
                        <span key={ci} className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                          <span className="material-symbols-outlined text-[10px]">description</span>
                          {cite}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-end gap-2 justify-start">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-[13px] text-indigo-600">smart_toy</span>
                </div>
                <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
                  {[0,1,2].map(j => (
                    <span key={j} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${j * 150}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-4 pb-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send(input)}
                placeholder="Ask about your exchange…"
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition disabled:opacity-60"
              />
              <button
                onClick={() => send(input)}
                disabled={loading || !input.trim()}
                className="w-10 h-10 bg-[#4343d5] text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 transition-colors disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-[18px]">send</span>
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
              <span className="material-symbols-outlined text-[13px]">lock</span>
              Answers based on {uniName} official documents only
            </p>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      <div className="col-span-4 flex items-start pt-0" style={{ maxHeight: '540px' }}>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden w-full">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[16px] text-indigo-600">lightbulb</span>
            <h2 className="font-display text-sm font-semibold text-[#0b1c30]">Suggested Questions</h2>
          </div>
          <div className="p-4 space-y-2">
            {suggestions === null ? (
              [1,2,3,4,5].map(i => (
                <div key={i} className="h-9 bg-slate-100 rounded-xl animate-pulse" />
              ))
            ) : (
              suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  disabled={loading}
                  className="w-full text-left px-3 py-2.5 text-xs font-medium text-[#464555] rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── Main component ─────────────────────────────────────────── */
export default function StudentAIAssistant() {
  return (
    <StudentLayout>
      <WorkInProgress
        icon="robot_2"
        title="AI Assistant"
        description="Ask anything about your exchange — visa, documents, housing — and get instant answers from your university's knowledge base."
      />
    </StudentLayout>
  );

  // ── Real page below — remove the return above to restore it ──
  // eslint-disable-next-line no-unreachable
  const { student } = useStudentContext();
  const { user }    = useAuth();
  const [activeTab, setActiveTab] = useState('own');

  const exchangeType   = student?.type ?? 'outgoing';
  const linkedUniId    = student?.partner_universities?.linked_university_id ?? null;
  const partnerUniName = student?.partner_universities?.name ?? 'Partner University';
  const homeUniName    = user?.university ?? 'Home University';

  // Own uni: ESMT's process (outgoing or incoming depending on student type)
  const ownUniversityId = user?.universityId ?? null;
  const ownType         = exchangeType;

  // Partner uni: opposite type
  const partnerType = exchangeType === 'outgoing' ? 'incoming' : 'outgoing';

  const hasDualAssistant = !!linkedUniId;

  return (
    <StudentLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">AI Assistant</h1>
        <p className="text-[#464555] mt-1">
          Ask questions about your exchange — answers come only from official university documents.
        </p>
      </div>

      {/* Tabs — only when partner is on Unixplore */}
      {hasDualAssistant && (
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit mb-6">
          <button
            onClick={() => setActiveTab('own')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'own'
                ? 'bg-white text-[#0b1c30] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="material-symbols-outlined text-[14px] mr-1.5 align-middle">school</span>
            {homeUniName}
          </button>
          <button
            onClick={() => setActiveTab('partner')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'partner'
                ? 'bg-white text-[#0b1c30] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="material-symbols-outlined text-[14px] mr-1.5 align-middle">location_city</span>
            {partnerUniName}
          </button>
        </div>
      )}

      {/* Chat panels */}
      {(!hasDualAssistant || activeTab === 'own') && (
        <ChatPanel
          key={`own-${ownUniversityId}`}
          universityId={ownUniversityId}
          type={ownType}
          uniName={homeUniName}
          fallbackSuggestions={exchangeType === 'outgoing' ? SUGGESTIONS_OUTGOING : SUGGESTIONS_INCOMING}
        />
      )}

      {hasDualAssistant && activeTab === 'partner' && (
        <ChatPanel
          key={`partner-${linkedUniId}`}
          universityId={linkedUniId}
          type={partnerType}
          uniName={partnerUniName}
          fallbackSuggestions={partnerType === 'incoming' ? SUGGESTIONS_INCOMING : SUGGESTIONS_OUTGOING}
        />
      )}
    </StudentLayout>
  );
}
